import BusinessIcon from "@mui/icons-material/Business";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { LoadUsers } from "../../../app/AppDataLoader";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { postWithAuth } from "../../../http";
import { showSuccess } from "../../notifications/notificationsSlice";
import { CounterpartyRoleTypeEnum, CounterpartyTagTypeEnum } from "../counterpartyApi";
import { validateEmail as validateEmailUtil } from "../../../app/Utils";
import { CountrySelect } from "../../administration/companyProfile/controls/CountrySelect";
import { selectAllUsers, User } from "../../users/usersSlice";

export type CounterpartyTypeEnum = "ORGANIZATION" | "PERSON";

export interface CreateCounterpartyDto {
    type: CounterpartyTypeEnum;
    displayName: string;
    legalName: string | null;
    countryCode: string | null;
    accountManagerFullName: string | null;
    accountManagerUserName: string | null;
    website: string | null;
    notes: string | null;
    roles: CounterpartyRoleTypeEnum[];
    tags: CounterpartyTagTypeEnum[];
    primaryEmail: string | null;
    primaryPhone: string | null;
}

export interface CounterpartyDetailsDto {
    publicId: string;
}

export interface CreateCounterpartyDialogProps {
    onSuccess: (counterparty: CounterpartyDetailsDto) => void;
    onCancel: () => void;
}

interface SectionProps {
    title: React.ReactNode;
    children: React.ReactNode;
}

const DialogSection = ({ title, children }: SectionProps) => {
    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
            })}
        >
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <ArrowRightIcon fontSize="small" />
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: 0.2,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            <Box sx={{ p: 2 }}>{children}</Box>
        </Box>
    );
};

const roleOptions: CounterpartyRoleTypeEnum[] = [
    "CUSTOMER",
    "SUPPLIER",
    "PARTNER",
    "REPRESENTATIVE",
    "SUBCONTRACTOR",
    "OTHER",
];

const typeOptions: CounterpartyTypeEnum[] = ["ORGANIZATION", "PERSON"];

export const CreateCounterpartyDialog = ({ onSuccess, onCancel }: CreateCounterpartyDialogProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const availableUsers = useAppSelector(selectAllUsers);

    const [type, setType] = useState<CounterpartyTypeEnum>("ORGANIZATION");
    const [displayName, setDisplayName] = useState("");
    const [legalName, setLegalName] = useState("");
    const [countryName, setCountryName] = useState("");
    const [countryCode, setCountryCode] = useState("");
    const [notes, setNotes] = useState("");
    const [roles, setRoles] = useState<CounterpartyRoleTypeEnum[]>([]);
    const [isVip, setIsVip] = useState(false);
    const [accountManager, setAccountManager] = useState<User | null>(null);
    const [accountManagerInput, setAccountManagerInput] = useState("");
    const [primaryEmail, setPrimaryEmail] = useState("");
    const [primaryPhone, setPrimaryPhone] = useState("");

    const [validationErrors, setValidationErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const displayNameError = validationErrors && displayName.trim() === "";
    const emailInvalid = primaryEmail.trim() !== "" && !validateEmailUtil(primaryEmail.trim());
    const primaryEmailError = validationErrors && emailInvalid;

    const accountManagerFullName = useMemo(() => {
        if (accountManager) {
            return `${accountManager.firstname ?? ""} ${accountManager.surname ?? ""}`.trim() || null;
        }

        return accountManagerInput.trim() || null;
    }, [accountManager, accountManagerInput]);

    const accountManagerUserName = useMemo(() => {
        return accountManager?.userName?.trim() || null;
    }, [accountManager]);

    const handleSaveClick = async () => {
        setValidationErrors(true);
        setServerError("");

        if (displayName.trim() === "" || emailInvalid) {
            return;
        }

        setSaving(true);
        try {
            const payload: CreateCounterpartyDto = {
                type,
                displayName: displayName.trim(),
                legalName: legalName.trim() || null,
                countryCode: countryCode.trim().toUpperCase() || null,
                accountManagerFullName: accountManagerFullName,
                accountManagerUserName: accountManagerUserName,
                website: null,
                notes: notes.trim() || null,
                roles,
                tags: isVip ? (["VIP"] as CounterpartyTagTypeEnum[]) : [],
                primaryEmail: primaryEmail.trim() || null,
                primaryPhone: primaryPhone.trim() || null,
            };

            const result = await dispatch(
                postWithAuth({
                    url: "api/counterparties",
                    payload,
                })
            );

            if (postWithAuth.fulfilled.match(result)) {
                const created = result.payload as CounterpartyDetailsDto;
                dispatch(showSuccess("saved"));
                onSuccess(created);
            } else {
                let errorMsg = "error";
                const p: any = (result as any).payload;
                if (p?.kind === "http" && p?.problem?.title) {
                    errorMsg = p.problem.title;
                }
                setServerError(errorMsg);
            }
        } catch {
            setServerError("error");
        } finally {
            setSaving(false);
        }
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        onCancel();
    };

    return (
        <Dialog maxWidth="md" fullWidth open onClose={handleDialogClose}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.100",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <BusinessIcon fontSize="small" color="primary" />
                <Localized id="counterparties-create-title">
                    <span>Nowy kontrahent</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="counterparties-base-information">
                                    <span>Base information</span>
                                </Localized>
                            }
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <FormControl fullWidth>
                                        <InputLabel id="counterparty-type-label">
                                            <Localized id="counterparties-type">
                                                <span>Typ</span>
                                            </Localized>
                                        </InputLabel>
                                        <Select
                                            labelId="counterparty-type-label"
                                            value={type}
                                            label={<Localized id="counterparties-type"><span>Type</span></Localized>}
                                            onChange={(e) => setType(e.target.value as CounterpartyTypeEnum)}
                                        >
                                            {typeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    <Localized id={`counterparties-type-${option}`}>
                                                        <span>{option}</span>
                                                    </Localized>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel
                                        control={<Switch checked={isVip} onChange={(e) => setIsVip(e.target.checked)} />}
                                        label={<Localized id="counterparties-vip"><span>VIP</span></Localized>}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        error={displayNameError}
                                        helperText={displayNameError && <AppRequiredMessage />}
                                        value={displayName}
                                        inputProps={{ maxLength: 200 }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                                        label={<Localized id="counterparties-displayName"><span>Nazwa wyświetlana</span></Localized>}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        value={legalName}
                                        inputProps={{ maxLength: 200 }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setLegalName(e.target.value)}
                                        label={<Localized id="counterparties-legalName"><span>Nazwa prawna</span></Localized>}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CountrySelect
                                        value={countryName}
                                        displayMode="name"
                                        onChange={(name, option) => {
                                            setCountryName(name ?? "");
                                            setCountryCode(option?.code ?? "");
                                        }}
                                        label={l10n.getString("counterparties-country", null, "Country")}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="counterparty-roles-label">
                                            <Localized id="counterparties-roles"><span>Role</span></Localized>
                                        </InputLabel>
                                        <Select
                                            labelId="counterparty-roles-label"
                                            multiple
                                            value={roles}
                                            onChange={(e) => setRoles(e.target.value as CounterpartyRoleTypeEnum[])}
                                            input={<OutlinedInput label={l10n.getString("counterparties-roles", null, "Roles")} />}
                                            renderValue={(selected) => (
                                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                                    {(selected as CounterpartyRoleTypeEnum[]).map((value) => (
                                                        <Chip
                                                            key={value}
                                                            size="small"
                                                            label={
                                                                <Localized id={`counterparties-role-${value}`}>
                                                                    <span>{value}</span>
                                                                </Localized>
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        >
                                            {roleOptions.map((role) => (
                                                <MenuItem key={role} value={role}>
                                                    <Checkbox checked={roles.indexOf(role) > -1} />
                                                    <Localized id={`counterparties-role-${role}`}>
                                                        <span>{role}</span>
                                                    </Localized>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </DialogSection>
                    </Grid>

                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="counterparties-contact-information">
                                    <span>Kontakt</span>
                                </Localized>
                            }
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        error={primaryEmailError}
                                        helperText={primaryEmailError ? <Localized id="invalid-email"><span>Invalid e-mail address</span></Localized> : undefined}
                                        value={primaryEmail}
                                        inputProps={{ maxLength: 200 }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setPrimaryEmail(e.target.value)}
                                        label={<Localized id="counterparties-email"><span>E-mail</span></Localized>}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        value={primaryPhone}
                                        inputProps={{ maxLength: 50 }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setPrimaryPhone(e.target.value)}
                                        label={<Localized id="counterparties-phone"><span>Telefon główny</span></Localized>}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <LoadUsers component={<Skeleton variant="text" />}>
                                        <Autocomplete<User, false, false, true>
                                            fullWidth
                                            freeSolo
                                            options={availableUsers}
                                            value={accountManager}
                                            inputValue={accountManagerInput}
                                            onChange={(_, newValue) => {
                                                if (!newValue || typeof newValue === "string") {
                                                    setAccountManager(null);
                                                    return;
                                                }

                                                setAccountManager(newValue);
                                                setAccountManagerInput(`${newValue.firstname} ${newValue.surname}`.trim());
                                            }}
                                            onInputChange={(_, newInputValue) => {
                                                setAccountManagerInput(newInputValue);
                                            }}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") {
                                                    return option;
                                                }

                                                return `${option.firstname} ${option.surname} (${option.email})`;
                                            }}
                                            isOptionEqualToValue={(option, value) => typeof value !== "string" && option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        <Localized id="counterparties-account-manager">
                                                            <span>Account manager</span>
                                                        </Localized>
                                                    }
                                                    helperText={accountManager?.position ?? " "}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        maxLength: 200,
                                                    }}
                                                />
                                            )}
                                        />
                                    </LoadUsers>
                                </Grid>
                            </Grid>
                        </DialogSection>
                    </Grid>

                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="counterparties-notes">
                                    <span>Notatki</span>
                                </Localized>
                            }
                        >
                            <TextField
                                fullWidth
                                value={notes}
                                inputProps={{ maxLength: 4000 }}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
                                label={<Localized id="counterparties-notes"><span>Notatki</span></Localized>}
                                multiline
                                minRows={3}
                            />
                        </DialogSection>
                    </Grid>
                </Grid>

                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ bgcolor: "background.paper", borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
